import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getListProductsQueryKey, useCreateProduct, useListCategories } from "@workspace/api-client-react";
import type { ProductInput } from "@workspace/api-client-react";
import { ArrowLeft, Check, ChevronsUpDown, CircleAlert, Columns3, Loader2, PackagePlus, Plus, RotateCcw, Rows3, WandSparkles } from "lucide-react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useBranch } from "@/context/branch";
import { useToast } from "@/hooks/use-toast";

type Draft = {
  name: string;
  categoryId: string;
  attribute1Label: string;
  attribute2Label: string;
  rows: string[];
  columns: string[];
  quantities: string[][];
  price: string;
  costPrice: string;
};

const initialDraft = (): Draft => ({
  name: "",
  categoryId: "",
  attribute1Label: "Size",
  attribute2Label: "Colour",
  rows: [""],
  columns: [""],
  quantities: [[""]],
  price: "",
  costPrice: "0",
});

function normalizeValue(value: string) {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, " ");
}

function skuPart(value: string) {
  const normalized = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (normalized) return normalized;
  return `u-${Array.from(value.trim(), character => character.codePointAt(0)!.toString(16)).join("-")}`;
}

function currency(value: number) {
  return `₦${value.toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function ProductMatrixEntry() {
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [validation, setValidation] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const { data: categories, isLoading: categoriesLoading, isError: categoriesError, refetch } = useListCategories();
  const createProduct = useCreateProduct();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { activeBranchId } = useBranch();
  const [, setLocation] = useLocation();

  const rowTotals = useMemo(
    () => draft.rows.map((_, rowIndex) => draft.columns.reduce((total, __, colIndex) => total + (Number(draft.quantities[rowIndex]?.[colIndex]) || 0), 0)),
    [draft.rows, draft.columns, draft.quantities],
  );
  const columnTotals = useMemo(
    () => draft.columns.map((_, colIndex) => draft.rows.reduce((total, __, rowIndex) => total + (Number(draft.quantities[rowIndex]?.[colIndex]) || 0), 0)),
    [draft.rows, draft.columns, draft.quantities],
  );
  const grandTotal = rowTotals.reduce((total, value) => total + value, 0);
  const variantCount = draft.quantities.reduce((total, row) => total + row.filter(value => Number(value) > 0).length, 0);

  const update = (patch: Partial<Draft>) => {
    setDraft(current => ({ ...current, ...patch }));
    if (validation.length) setValidation([]);
  };

  const changeQuantity = (rowIndex: number, colIndex: number, value: string) => {
    setDraft(current => ({
      ...current,
      quantities: current.quantities.map((row, ri) =>
        ri === rowIndex ? row.map((quantity, ci) => ci === colIndex ? value : quantity) : row,
      ),
    }));
    if (validation.length) setValidation([]);
  };

  const setAxisValue = (axis: "rows" | "columns", index: number, value: string) => {
    update({ [axis]: draft[axis].map((item, i) => i === index ? value : item) } as Partial<Draft>);
  };

  const addRow = () => update({
    rows: [...draft.rows, ""],
    quantities: [...draft.quantities, Array(draft.columns.length).fill("")],
  });
  const addColumn = () => update({
    columns: [...draft.columns, ""],
    quantities: draft.quantities.map(row => [...row, ""]),
  });

  const clearDraft = () => {
    setDraft(initialDraft());
    setValidation([]);
  };

  const validate = () => {
    const issues: string[] = [];
    if (!draft.name.trim()) issues.push("Enter a style name.");
    if (!draft.categoryId || !categories?.some(category => String(category.id) === draft.categoryId)) issues.push("Choose a category.");
    if (!draft.attribute1Label.trim() || !draft.attribute2Label.trim()) issues.push("Enter a label for both attributes.");
    const rowValues = draft.rows.map(value => value.trim());
    const colValues = draft.columns.map(value => value.trim());
    if (rowValues.some(value => !value)) issues.push("Enter a value in every row header (for example, S, M, or L).");
    if (colValues.some(value => !value)) issues.push("Enter a value in every column header (for example, Black or White).");
    const duplicates = (values: string[]) => {
      const normalized = values.map(normalizeValue);
      return normalized.some((value, index) => normalized.indexOf(value) !== index);
    };
    if (rowValues.every(Boolean) && duplicates(rowValues)) issues.push(`${draft.attribute1Label.trim() || "Row"} values must be unique.`);
    if (colValues.every(Boolean) && duplicates(colValues)) issues.push(`${draft.attribute2Label.trim() || "Column"} values must be unique.`);
    const price = Number(draft.price);
    const cost = Number(draft.costPrice);
    if (!draft.price.trim() || !Number.isFinite(price) || price <= 0) issues.push("Selling price must be greater than zero.");
    if (!Number.isFinite(cost) || cost < 0) issues.push("Enter a valid cost price of zero or more.");
    if (draft.quantities.some(row => row.some(quantity => {
      const value = quantity.trim();
      return value !== "" && !/^[-–—]$/.test(value) && !/^\d+$/.test(value);
    }))) {
      issues.push("Quantities must be whole numbers of zero or more. Use a dash, 0, or a blank cell when you have none.");
    }
    if (!draft.quantities.some(row => row.some(quantity => Number(quantity) > 0))) issues.push("Enter a quantity greater than zero in at least one cell.");
    setValidation(issues);
    return issues.length === 0;
  };

  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) {
      document.getElementById("matrix-validation")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSaving(true);
    try {
      const response = await fetch("/api/products/generate-barcode", {
        method: "POST",
        credentials: "include",
      });
      const result: unknown = await response.json();
      const barcode = result && typeof result === "object" && "barcode" in result ? String(result.barcode ?? "").trim() : "";
      if (!response.ok || !barcode) {
        const message = result && typeof result === "object" && "error" in result ? String(result.error) : "Could not generate a style SKU.";
        throw new Error(message);
      }

      const price = Number(draft.price);
      const variants = draft.rows.flatMap((row, rowIndex) =>
        draft.columns.flatMap((column, colIndex) => {
          const stock = Number(draft.quantities[rowIndex]?.[colIndex]) || 0;
          if (stock <= 0) return [];
          return [{
            id: crypto.randomUUID(),
            label: `${column.trim()} / ${row.trim()}`,
            sku: `${barcode}-${skuPart(column)}-${skuPart(row)}`,
            price,
            stock,
            attribute1Label: draft.attribute1Label.trim(),
            attribute1Value: row.trim(),
            attribute2Label: draft.attribute2Label.trim(),
            attribute2Value: column.trim(),
          }];
        }),
      );
      const proposedSkus = new Set([barcode.toLowerCase()]);
      const duplicateSku = variants.find(variant => {
        const key = variant.sku.trim().toLowerCase();
        if (proposedSkus.has(key)) return true;
        proposedSkus.add(key);
        return false;
      });
      if (duplicateSku) {
        setSaving(false);
        setValidation(["Two or more attribute combinations produce the same SKU. Change the row or column values and try again."]);
        return;
      }
      const payload: ProductInput = {
        name: draft.name.trim(),
        sku: barcode,
        categoryId: Number(draft.categoryId),
        price,
        costPrice: Number(draft.costPrice),
        stock: grandTotal,
        unit: "piece",
        hasVariants: true,
        variants: JSON.stringify(variants),
        ...(activeBranchId != null ? { branchId: activeBranchId } : {}),
      };
      createProduct.mutate({ data: payload }, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getListProductsQueryKey() });
          toast({ title: "Style created", description: `${draft.name.trim()} is ready with ${variants.length} variants.` });
          setLocation("/inventory");
        },
        onError: error => {
          setSaving(false);
          toast({
            title: "Failed to save style",
            description: error instanceof Error ? error.message : "Please check your connection and try again.",
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      setSaving(false);
      toast({
        title: "Style SKU not generated",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const controlClass = "border-[#c9d8d2] bg-[#fffefa] focus-visible:ring-[#0e6b53]";

  return (
    <main className="min-h-[100dvh] bg-[#f4f6f1] px-4 pb-28 pt-5 text-[#172e29] sm:px-7 sm:pt-8 lg:px-10">
      <div className="mx-auto max-w-[1440px]">
        <Link href="/inventory" className="mb-5 inline-flex items-center gap-2 text-sm font-semibold text-[#52716a] transition-colors hover:text-[#0e6b53]">
          <ArrowLeft className="h-4 w-4" /> Inventory
        </Link>

        <form onSubmit={save} noValidate>
          <header className="mb-7 flex flex-col justify-between gap-5 border-b border-[#dce5df] pb-6 sm:flex-row sm:items-end">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#cfe0d7] bg-[#e8f1eb] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#28624f]">
                <PackagePlus className="h-3.5 w-3.5" /> Inventory setup
              </div>
              <h1 className="text-3xl font-bold tracking-[-0.04em] text-[#173b30] sm:text-[2.5rem]">Create style matrix</h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-[#60766f]">Set up one parent style, then count only the combinations you actually have on the shelf.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={clearDraft} disabled={saving} className="gap-2 border-[#c9d8d2] bg-[#fffefa] text-[#39564e]">
                <RotateCcw className="h-4 w-4" /> Cancel
              </Button>
              <Button type="submit" disabled={saving || categoriesLoading} className="gap-2 bg-[#12664f] px-5 text-white hover:bg-[#0e5744]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {saving ? "Saving style…" : "Save style"}
              </Button>
            </div>
          </header>

          {validation.length > 0 && (
            <section id="matrix-validation" role="alert" className="mb-6 rounded-xl border border-[#dfb7a6] bg-[#fff5ef] p-4 text-sm text-[#7e3928]">
              <div className="flex items-center gap-2 font-bold"><CircleAlert className="h-4 w-4" /> Check these details before saving</div>
              <ul className="mt-2 list-inside list-disc space-y-1">{validation.map((issue, index) => <li key={`${issue}-${index}`}>{issue}</li>)}</ul>
            </section>
          )}

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.62fr)]">
            <section className="rounded-2xl border border-[#d8e3dc] bg-[#fffefa] p-5 shadow-[0_8px_24px_rgba(26,62,48,0.04)] sm:p-6">
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#648078]">01 / Style details</p>
                  <h2 className="mt-1 text-lg font-bold text-[#193b31]">Parent product</h2>
                </div>
                <span className="rounded-lg bg-[#f0f4ee] px-2.5 py-1.5 font-mono text-[11px] font-semibold text-[#668078]">SKU generated on save</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="space-y-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold">Style name <span className="text-[#b2543b]">*</span></span>
                  <Input autoFocus value={draft.name} onChange={event => update({ name: event.target.value })} placeholder="e.g. Classic Oxford Shirt" className={controlClass} />
                </label>
                <label className="space-y-1.5 sm:col-span-2">
                  <span className="text-sm font-semibold">Category <span className="text-[#b2543b]">*</span></span>
                  {categoriesLoading ? (
                    <div className="h-10 animate-pulse rounded-md bg-[#edf2ed]" />
                  ) : categoriesError ? (
                    <div className="flex min-h-10 items-center justify-between gap-3 rounded-md border border-[#dfb7a6] bg-[#fff7f2] px-3 text-sm text-[#83402e]">
                      <span>Categories could not be loaded.</span><Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>Retry</Button>
                    </div>
                  ) : (
                    <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={categoryOpen}
                          data-testid="button-select-matrix-category"
                          className={`w-full justify-between font-normal ${controlClass}`}
                        >
                          {categories?.find(category => String(category.id) === draft.categoryId)?.name ?? "Select a category"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search categories…" data-testid="input-search-matrix-category" />
                          <CommandList>
                            <CommandEmpty>
                              {categories?.length ? "No matching category." : "No categories available."}
                            </CommandEmpty>
                            <CommandGroup>
                              {(categories ?? []).map(category => (
                                <CommandItem
                                  key={category.id}
                                  value={category.name}
                                  onSelect={() => {
                                    update({ categoryId: String(category.id) });
                                    setCategoryOpen(false);
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${draft.categoryId === String(category.id) ? "opacity-100" : "opacity-0"}`} />
                                  {category.name}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </label>
              </div>
            </section>

            <section className="rounded-2xl border border-[#d8e3dc] bg-[#fffefa] p-5 shadow-[0_8px_24px_rgba(26,62,48,0.04)] sm:p-6">
              <div className="mb-5">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#648078]">02 / Shared pricing</p>
                <h2 className="mt-1 text-lg font-bold text-[#193b31]">Same price across variants</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                <label className="space-y-1.5">
                  <span className="text-sm font-semibold">Selling price <span className="text-[#b2543b]">*</span></span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#668078]">₦</span>
                    <Input type="number" min="0.01" step="0.01" value={draft.price} onChange={event => update({ price: event.target.value })} placeholder="0.00" className={`${controlClass} pl-8`} />
                  </div>
                </label>
                <label className="space-y-1.5">
                  <span className="text-sm font-semibold">Cost price <span className="font-normal text-[#81928c]">(optional)</span></span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#668078]">₦</span>
                    <Input type="number" min="0" step="0.01" value={draft.costPrice} onChange={event => update({ costPrice: event.target.value })} placeholder="0.00" className={`${controlClass} pl-8`} />
                  </div>
                </label>
              </div>
              <p className="mt-4 border-t border-[#e6ece7] pt-3 text-xs leading-5 text-[#72857f]">The selling price is copied to every created variant. Cost stays on the parent style.</p>
            </section>
          </div>

          <section className="mt-5 overflow-hidden rounded-2xl border border-[#d8e3dc] bg-[#fffefa] shadow-[0_8px_24px_rgba(26,62,48,0.04)]">
            <div className="flex flex-col justify-between gap-4 border-b border-[#e3ebe5] p-5 sm:flex-row sm:items-center sm:px-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#648078]">03 / Variant dimensions</p>
                <h2 className="mt-1 text-lg font-bold text-[#193b31]">Build your stock grid</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={addRow} className="gap-2 border-[#c9d8d2] text-[#31584b]"><Plus className="h-4 w-4" /><Rows3 className="h-4 w-4" /> Add Row</Button>
                <Button type="button" variant="outline" onClick={addColumn} className="gap-2 border-[#c9d8d2] text-[#31584b]"><Plus className="h-4 w-4" /><Columns3 className="h-4 w-4" /> Add Column</Button>
              </div>
            </div>
            <div className="grid gap-4 border-b border-[#e3ebe5] bg-[#f8faf6] p-5 sm:grid-cols-2 sm:px-6">
              <div className="rounded-xl border border-[#dbe5de] bg-[#fffefa] p-3.5">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-[#688078]">Row attribute name</label>
                <Input maxLength={80} value={draft.attribute1Label} onChange={event => update({ attribute1Label: event.target.value })} placeholder="e.g. Size" aria-label="Row attribute name" className={`${controlClass} mb-3 font-semibold`} />
                <p className="text-xs text-[#7a8d86]">Name the property here, such as Size. Enter S, M, L in the row headers below.</p>
              </div>
              <div className="rounded-xl border border-[#dbe5de] bg-[#fffefa] p-3.5">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-[#688078]">Column attribute name</label>
                <Input maxLength={80} value={draft.attribute2Label} onChange={event => update({ attribute2Label: event.target.value })} placeholder="e.g. Colour" aria-label="Column attribute name" className={`${controlClass} mb-3 font-semibold`} />
                <p className="text-xs text-[#7a8d86]">Name the property here, such as Colour. Enter Black and White once in the column headers; leave an unavailable quantity cell blank or enter 0.</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] border-collapse text-sm">
                <thead>
                  <tr className="bg-[#edf3ee]">
                    <th className="w-64 border-b border-r border-[#dce6de] p-3 text-left align-top">
                      <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#71867e]">{draft.attribute1Label.trim() || "Row value"}</span>
                      <span className="text-xs font-semibold text-[#637970]">Enter each {draft.attribute1Label.trim().toLowerCase() || "row"} value</span>
                    </th>
                    {draft.columns.map((column, colIndex) => (
                      <th key={`column-${colIndex}`} className="min-w-40 border-b border-r border-[#dce6de] p-3 text-left align-top">
                        <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#71867e]">Column {String(colIndex + 1).padStart(2, "0")}</span>
                         <Input maxLength={120} aria-label={`${draft.attribute2Label || "Attribute 2"} column ${colIndex + 1}`} value={column} onChange={event => setAxisValue("columns", colIndex, event.target.value)} placeholder="Enter a value" className={`h-9 ${controlClass}`} />
                      </th>
                    ))}
                    <th className="w-24 border-b border-[#dce6de] p-3 text-right align-bottom text-xs font-bold uppercase tracking-wide text-[#688078]">Row total</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.rows.map((row, rowIndex) => (
                    <tr key={`row-${rowIndex}`} className="group">
                      <th className="border-b border-r border-[#e5ece7] bg-[#f8faf6] p-3 text-left">
                        <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.12em] text-[#83958e]">Row {String(rowIndex + 1).padStart(2, "0")}</span>
                         <Input maxLength={120} aria-label={`${draft.attribute1Label || "Attribute 1"} row ${rowIndex + 1}`} value={row} onChange={event => setAxisValue("rows", rowIndex, event.target.value)} placeholder="Enter a value" className={`h-9 ${controlClass}`} />
                      </th>
                      {draft.columns.map((column, colIndex) => {
                        const quantity = draft.quantities[rowIndex]?.[colIndex] ?? "";
                        const invalid = quantity !== "" && !/^\d+$/.test(quantity);
                        return (
                          <td key={`cell-${rowIndex}-${colIndex}`} className="border-b border-r border-[#e5ece7] p-3">
                          <label className="sr-only">{`${column || `Column ${colIndex + 1}`} / ${row || `Row ${rowIndex + 1}`} quantity`}</label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={quantity}
                            onChange={event => changeQuantity(rowIndex, colIndex, event.target.value)}
                            placeholder="0"
                            aria-label={`${column || `Column ${colIndex + 1}`} / ${row || `Row ${rowIndex + 1}`} quantity`}
                            aria-invalid={invalid}
                            className={`h-10 text-center font-mono text-base tabular-nums ${controlClass} ${invalid ? "border-[#b2543b] focus-visible:ring-[#b2543b]" : ""}`}
                          />
                          </td>
                        );
                      })}
                      <td className="border-b border-[#e5ece7] px-4 text-right font-mono font-semibold tabular-nums text-[#31584b]">{rowTotals[rowIndex].toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#f0f5f0]">
                    <th className="border-r border-[#dce6de] px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-[#567168]">Column total</th>
                    {columnTotals.map((total, index) => <td key={`total-${index}`} className="border-r border-[#dce6de] px-4 py-3 text-center font-mono font-semibold tabular-nums text-[#31584b]">{total.toLocaleString()}</td>)}
                    <td className="px-4 py-3 text-right font-mono text-base font-bold tabular-nums text-[#12664f]">{grandTotal.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="flex flex-col gap-2 border-t border-[#e3ebe5] px-5 py-3.5 text-xs text-[#72857f] sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <span>Row and column headers apply across the grid. Leave a quantity cell blank (or enter 0) when you do not stock that combination; it will not be saved as a variant.</span>
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#31584b]"><WandSparkles className="h-3.5 w-3.5" /> {variantCount} variant{variantCount === 1 ? "" : "s"} · {grandTotal.toLocaleString()} units</span>
            </div>
          </section>

          <footer className="mt-6 flex flex-col gap-4 rounded-2xl border border-[#d8e3dc] bg-[#eaf1eb] p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <p className="text-sm font-bold text-[#193b31]">Ready to add this style?</p>
              <p className="mt-1 text-xs text-[#60766f]">{variantCount} stocked combinations · {grandTotal.toLocaleString()} total units{draft.price && Number(draft.price) > 0 ? ` · ${currency(Number(draft.price))} each` : ""}</p>
            </div>
            <Button type="submit" disabled={saving || categoriesLoading} className="gap-2 bg-[#12664f] px-6 text-white hover:bg-[#0e5744]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} {saving ? "Saving style…" : "Save style"}
            </Button>
          </footer>
        </form>
      </div>
    </main>
  );
}
